import { toApiError } from "@/lib/api/errors";
import { API_BASE_URL } from "@/lib/config";
import type { AuthTokens } from "./session";

// Auth endpoints use raw fetch (not the openapi client) so they never carry a
// stale Authorization header and stay decoupled from generated types. They are
// rate-limited server-side (5 rps/IP, burst 10) per internal/api/middleware.
const baseUrl = API_BASE_URL;

async function postAuth<T>(
	path: string,
	body: unknown,
	extraHeaders?: Record<string, string>,
): Promise<T> {
	let response: Response;
	try {
		response = await fetch(`${baseUrl}${path}`, {
			method: "POST",
			headers: { "Content-Type": "application/json", ...extraHeaders },
			body: JSON.stringify(body),
		});
	} catch {
		throw toApiError(undefined, undefined);
	}
	if (!response.ok) {
		const errorBody = await response.json().catch(() => undefined);
		throw toApiError(errorBody, response);
	}
	if (response.status === 204) return undefined as T;
	return (await response.json()) as T;
}

export interface LoginInput {
	email: string;
	password: string;
	/** Cloudflare Turnstile token; forwarded for future backend verification. */
	turnstileToken?: string;
}

export function login(input: LoginInput): Promise<AuthTokens> {
	const headers = input.turnstileToken
		? { "cf-turnstile-response": input.turnstileToken }
		: undefined;
	return postAuth<AuthTokens>(
		"/api/v1/auth/login",
		{ email: input.email, password: input.password },
		headers,
	);
}

export function refresh(refreshToken: string): Promise<AuthTokens> {
	return postAuth<AuthTokens>("/api/v1/auth/refresh", {
		refresh_token: refreshToken,
	});
}

export function logout(refreshToken: string): Promise<void> {
	return postAuth<void>("/api/v1/auth/logout", { refresh_token: refreshToken });
}
